import React, { useState, useEffect } from 'react';
import { adminService } from '../../services/adminService';
import './PaymentSettings.css';

interface PaymentSettingsData {
  PaymentSettings: {
    ExcludeCurrentYearFromPayments: boolean;
    RequireFullPayment: boolean;
    CurrentYear: number;
    ExclusionMessage?: string;
  };
  GeneralSettings?: {
    SystemMaintenanceMode: boolean;
    MaintenanceMessage: string;
  };
  lastUpdated?: string;
  updatedBy?: string;
}

const PaymentSettings: React.FC = () => {
  const [settings, setSettings] = useState<PaymentSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setMessage({ type: 'success', text: 'Cargando configuraciones...' });

      const response = await adminService.getSystemSettings();
      setSettings(response);

      setMessage(null);
    } catch (error: any) {
      setMessage({ type: 'error', text: `Error al cargar configuraciones: ${error.message}` });
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!settings) return;

    if (!window.confirm(
      '¿Está seguro de que desea guardar estos cambios?\n\n' +
      'Esto afectará inmediatamente cómo los usuarios pueden realizar pagos.'
    )) {
      return;
    }

    try {
      setSaving(true);
      setMessage({ type: 'warning', text: 'Guardando configuraciones...' });

      await adminService.updatePaymentSettings({
        excludeCurrentYearFromPayments: settings.PaymentSettings.ExcludeCurrentYearFromPayments,
        requireFullPayment: settings.PaymentSettings.RequireFullPayment
      });

      setMessage({ type: 'success', text: '✅ Configuraciones guardadas exitosamente' });

      // Recargar para confirmar
      setTimeout(() => {
        loadSettings();
      }, 2000);
    } catch (error: any) {
      setMessage({ type: 'error', text: `❌ Error al guardar: ${error.message}` });
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (setting: 'excludeCurrentYear' | 'requireFullPayment') => {
    if (!settings) return;

    setSettings({
      ...settings,
      PaymentSettings: {
        ...settings.PaymentSettings,
        ExcludeCurrentYearFromPayments: setting === 'excludeCurrentYear'
          ? !settings.PaymentSettings.ExcludeCurrentYearFromPayments
          : settings.PaymentSettings.ExcludeCurrentYearFromPayments,
        RequireFullPayment: setting === 'requireFullPayment'
          ? !settings.PaymentSettings.RequireFullPayment
          : settings.PaymentSettings.RequireFullPayment,
        CurrentYear: settings.PaymentSettings.CurrentYear,
        ExclusionMessage: settings.PaymentSettings.ExclusionMessage
      }
    });
  };

  if (loading && !settings) {
    return (
      <div className="payment-settings">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Cargando configuraciones...</p>
        </div>
      </div>
    );
  }

  if (!settings || !settings.PaymentSettings) {
    return (
      <div className="payment-settings">
        <div className="settings-header">
          <h2>💳 Configuraciones de Pagos</h2>
          <p className="settings-description">
            Configure cómo los usuarios pueden realizar pagos en el sistema.
          </p>
        </div>
        <div className="alert alert-error">
          ❌ No se pudieron cargar las configuraciones. Por favor, verifica que el backend esté funcionando correctamente.
        </div>
        <div className="settings-actions">
          <button className="btn btn-reload" onClick={loadSettings}>
            🔄 Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-settings">
      <div className="settings-header">
        <h2>💳 Configuraciones de Pagos</h2>
        <p className="settings-description">
          Configure cómo los usuarios pueden realizar pagos en el sistema.
        </p>
      </div>

      {message && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      <>
          {/* Excluir Año Actual */}
          <div className="setting-card">
            <div className="setting-header">
              <div className="setting-info">
                <h3>Excluir Año Actual de Pagos</h3>
                <span className={`badge ${settings.PaymentSettings.ExcludeCurrentYearFromPayments ? 'active' : 'inactive'}`}>
                  {settings.PaymentSettings.ExcludeCurrentYearFromPayments ? 'ACTIVO' : 'INACTIVO'}
                </span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.PaymentSettings.ExcludeCurrentYearFromPayments}
                  onChange={() => handleToggle('excludeCurrentYear')}
                  disabled={saving}
                />
                <span className="slider"></span>
              </label>
            </div>
            <div className="setting-description">
              <p>
                Cuando está <strong>ACTIVO</strong>: Los usuarios <strong>NO pueden pagar</strong> el año actual ({settings.PaymentSettings.CurrentYear}).
                Solo pueden pagar deudas de años anteriores.
              </p>
              <p className="use-case">
                <strong>Caso de uso:</strong> Activar esta opción temporalmente para procesar pagos pendientes de años anteriores
                sin incluir el cobro del año {settings.PaymentSettings.CurrentYear}.
              </p>
            </div>
            <div className="current-year-info">
              <strong>📅 Año actual del sistema: {settings.PaymentSettings.CurrentYear}</strong>
              <br />
              {settings.PaymentSettings.ExcludeCurrentYearFromPayments && (
                <span className="warning-text">
                  ⚠️ El año {settings.PaymentSettings.CurrentYear} está siendo excluido de todos los pagos.
                </span>
              )}
            </div>
          </div>

          {/* Requerir Pago Completo */}
          <div className="setting-card">
            <div className="setting-header">
              <div className="setting-info">
                <h3>Requerir Pago Completo</h3>
                <span className={`badge ${settings.PaymentSettings.RequireFullPayment ? 'active' : 'inactive'}`}>
                  {settings.PaymentSettings.RequireFullPayment ? 'ACTIVO' : 'INACTIVO'}
                </span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.PaymentSettings.RequireFullPayment}
                  onChange={() => handleToggle('requireFullPayment')}
                  disabled={saving}
                />
                <span className="slider"></span>
              </label>
            </div>
            <div className="setting-description">
              <p>
                Cuando está <strong>ACTIVO</strong>: El sistema calcula automáticamente el pago incluyendo <strong>TODOS</strong> los años adeudados.
                Los usuarios deben pagar la deuda completa en una sola transacción.
              </p>
              <p>
                Cuando está <strong>INACTIVO</strong>: El sistema permite cálculos de pago más flexibles (comportamiento definido en el backend).
              </p>
              <p className="use-case">
                <strong>⚠️ Nota:</strong> Solo los administradores pueden modificar manualmente los años a pagar desde "Transacciones Fallidas".
                Esta configuración NO permite que los usuarios finales elijan qué años pagar.
              </p>
            </div>
          </div>

          {/* Botones de Acción */}
          <div className="settings-actions">
            <button
              className="btn btn-save"
              onClick={saveSettings}
              disabled={saving || loading}
            >
              {saving ? '💾 Guardando...' : '💾 Guardar Cambios'}
            </button>
            <button
              className="btn btn-reload"
              onClick={loadSettings}
              disabled={saving || loading}
            >
              🔄 Recargar
            </button>
          </div>

        {/* Información Adicional */}
        {settings.lastUpdated && (
          <div className="settings-info-box">
            <h4>ℹ️ Información</h4>
            <p><strong>Última actualización:</strong> {new Date(settings.lastUpdated).toLocaleString('es-CO')}</p>
            {settings.updatedBy && <p><strong>Actualizado por:</strong> {settings.updatedBy}</p>}
          </div>
        )}
      </>
    </div>
  );
};

export default PaymentSettings;
